import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";

import OurJourneyCard from "../OurJourneyCard/OurJourneyCard";
import {
  OurJourneyArrow,
  OurJourneyCardContainer,
  OurJourneyInner,
  OurJourneySection,
  OurJourneyWrapper,
} from "./OurJourney.styles";

gsap.registerPlugin(ScrollTrigger);

interface IOurJourney {
  heading: string;
  subHeading: string;
  arrowSrc: string;
  arrowAlt: string;
  pinIconSrc: string;
  journeyData: {
    id: number;
    year: string;
    description: string;
    overall: boolean;
    pinIcon: boolean;
    bgColor: string;
    footerText: string;
  }[];
}

const OurJourney = ({
  heading,
  subHeading,
  journeyData,
  arrowSrc,
  arrowAlt,
  pinIconSrc,
}: IOurJourney) => {
  const mainRef = useRef<any>();
  const headingRef = useRef<any>();
  const subHeadingRef = useRef<any>();
  const cardRef1 = useRef<any>();
  const cardRef2 = useRef<any>();
  const cardRef3 = useRef<any>();
  const cardRef4 = useRef<any>();

  const arr = [cardRef1, cardRef2, cardRef3, cardRef4];

  useEffect(() => {
    const cards = [
      cardRef4.current,
      cardRef3.current,
      cardRef2.current,
      cardRef1.current,
    ];

    gsap.from(headingRef.current, {
      scrollTrigger: {
        trigger: mainRef.current,
        start: "top 70%",
        toggleActions: "restart resume none reverse",
      },
      y: 100,
      opacity: 0,
      ease: "ease-in-out",
      duration: 1,
    });
    gsap.from(subHeadingRef.current, {
      scrollTrigger: {
        trigger: mainRef.current,
        start: "top 70%",
        toggleActions: "restart resume none reverse",
      },
      y: 100,
      opacity: 0,
      ease: "ease-in-out",
      duration: 1,
    });

    // background grid animation
    gsap.from(mainRef.current, {
      backgroundPosition: () =>
        `0% -${mainRef.current.getBoundingClientRect().top / 1.5}px`,
      ease: "none",
      scrollTrigger: {
        trigger: mainRef.current,
        //start: () => i ? "top bottom" : "top top",
        //end: "bottom top",
        scrub: true,
        invalidateOnRefresh: true, // to make it responsive
      },
    });

    ScrollTrigger.matchMedia({
      "(min-width: 769px)": function () {
        //journey section small to large animation
        gsap.from(mainRef.current, {
          scrollTrigger: {
            trigger: mainRef.current,
            start: "top 90%",
            end: "top 50%",
            toggleActions: "restart resume none reverse",
            scrub: true,
          },
          width: 1200,
          borderRadius: "40px",
          ease: "ease-in-out",
          duration: 1,
        });

        gsap.from(cards, {
          scrollTrigger: {
            trigger: mainRef.current,
            start: "top 40%",
            toggleActions: "restart resume none reverse",
            scrub: true,
            end: "bottom bottom",
            onLeave: () => {
              gsap.to(cards, {
                scrollTrigger: {
                  trigger: mainRef.current,
                  start: "top top",
                  end: "bottom top",
                  toggleActions: "restart resume none reverse",
                  scrub: true,
                },
                y: -300,
                opacity: 0,
                stagger: 0.3,
                ease: "ease-in-out",
                duration: 3,
              });
            },
          },
          y: 300,
          opacity: 0,
          stagger: 0.5,
          ease: "ease-in-out",
          duration: 4,
        });
      },
      "(max-width: 767px)": function () {
        gsap.from(cards, {
          scrollTrigger: {
            trigger: mainRef.current,
            start: "top 40%",
            toggleActions: "restart resume none reverse",
            scrub: true,
            end: "top 20%",
          },
          y: 300,
          opacity: 0,
          stagger: 0.5,
          ease: "ease-in-out",
          duration: 4,
        });
      },
    });
  }, []);

  return (
    <OurJourneySection ref={mainRef} id="ourJourney">
      <OurJourneyInner>
        <OurJourneyWrapper>
          {heading && (
            <h2 id="heading" ref={headingRef}>
              {heading}
            </h2>
          )}
          {subHeading && (
            <h3 id="subHeading" ref={subHeadingRef}>
              {subHeading}
            </h3>
          )}
          {journeyData && journeyData.length > 0 && (
            <OurJourneyCardContainer>
              {journeyData.map((item: any, index: number) => (
                <OurJourneyCard
                  cardRef={arr[index]}
                  data={item}
                  key={item.id}
                  pinIconSrc={pinIconSrc}
                />
              ))}
            </OurJourneyCardContainer>
          )}
        </OurJourneyWrapper>
      </OurJourneyInner>
      <OurJourneyArrow src={arrowSrc} alt={arrowAlt} />
    </OurJourneySection>
  );
};

export default OurJourney;
